import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { 
  Coins, 
  CreditCard, 
  History, 
  ArrowLeft,
  Check,
  Star,
  Zap,
  Crown,
  TrendingUp,
  TrendingDown,
  Gift
} from "lucide-react";

const CreditsPage = ({ user }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [activeTab, setActiveTab] = useState("packages");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, balanceRes, historyRes] = await Promise.all([
        axios.get(`${API}/credits/packages`),
        axios.get(`${API}/credits/balance`),
        axios.get(`${API}/credits/history`)
      ]);
      
      setPackages(packagesRes.data);
      setBalance(balanceRes.data.balance);
      setHistory(historyRes.data);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        packageId: packageId
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setBalance(response.data.newBalance);
        fetchData(); // Refresh history
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Satın alma başarısız");
    } finally {
      setPurchasing(null);
    }
  };

  const getPackageIcon = (index) => {
    const icons = [Coins, Star, Zap, Crown];
    return icons[index] || Coins;
  };

  const getPackageGradient = (index) => {
    const gradients = [
      "from-amber-500 to-orange-500",
      "from-blue-500 to-indigo-500",
      "from-purple-500 to-pink-500",
      "from-emerald-500 to-teal-500"
    ];
    return gradients[index] || gradients[0];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1f2224]' : ''}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1f2224]' : 'bg-gradient-to-br from-slate-50 via-white to-emerald-50'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Coins className="h-8 w-8" />
                Kredi Mağazası
              </h1>
              <p className="text-emerald-100 mt-1">
                Kredi satın al, kulüp özelliklerini aç
              </p>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-4">
              <p className="text-emerald-100 text-sm">Mevcut Bakiye</p>
              <p className="text-3xl font-bold flex items-center gap-2">
                <Coins className="h-6 w-6" />
                {balance}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className={`mb-8 p-4 rounded-xl border ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${isDark ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                <Star className={`h-6 w-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <div>
                <p className={`font-semibold ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>Bireysel Ünvanlar</p>
                <p className={`text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Profilini öne çıkar, ünvan sahibi ol!</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/titles")}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Crown className="h-4 w-4 mr-2" />
              Ünvanları Gör
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <Button
            variant={activeTab === "packages" ? "default" : "outline"}
            onClick={() => setActiveTab("packages")}
            className={activeTab === "packages" ? "bg-emerald-600 hover:bg-emerald-700" : isDark ? 'border-[#3a3d40] text-white hover:bg-[#2a2d2f]' : ''}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Paketler
          </Button>
          <Button
            variant={activeTab === "history" ? "default" : "outline"}
            onClick={() => setActiveTab("history")}
            className={activeTab === "history" ? "bg-emerald-600 hover:bg-emerald-700" : isDark ? 'border-[#3a3d40] text-white hover:bg-[#2a2d2f]' : ''}
          >
            <History className="h-4 w-4 mr-2" />
            Geçmiş
          </Button>
        </div>

        {/* Packages Tab */}
        {activeTab === "packages" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, index) => {
              const Icon = getPackageIcon(index);
              const gradient = getPackageGradient(index);
              
              return (
                <Card 
                  key={pkg.id} 
                  className={`relative overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 ${pkg.popular ? 'ring-2 ring-blue-500' : ''} ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-semibold">
                      EN POPÜLER
                    </div>
                  )}
                  
                  <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                  
                  <CardHeader className="text-center pb-2">
                    <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`}>
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className={`text-xl ${isDark ? 'text-white' : ''}`}>{pkg.name}</CardTitle>
                    <CardDescription>
                      <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{pkg.credits}</span>
                      <span className={`ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>kredi</span>
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="text-center space-y-4">
                    <div>
                      <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{pkg.price}₺</p>
                      {pkg.discount > 0 && (
                        <Badge variant="secondary" className={`mt-1 ${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                          %{pkg.discount} İndirim
                        </Badge>
                      )}
                    </div>
                    
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      1 kredi = {(pkg.price / pkg.credits).toFixed(2)}₺
                    </div>
                    
                    <Button 
                      className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90 text-white`}
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasing === pkg.id}
                    >
                      {purchasing === pkg.id ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          İşleniyor...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Satın Al
                        </span>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <Card className={isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                <History className="h-5 w-5" />
                İşlem Geçmişi
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Coins className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Henüz işlem geçmişi yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((transaction) => (
                    <div 
                      key={transaction.id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${isDark ? 'bg-[#3a3d40] hover:bg-[#4a4d50]' : 'bg-gray-50 hover:bg-gray-100'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          transaction.amount > 0 
                            ? 'bg-emerald-100 text-emerald-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {transaction.type === 'purchase' ? (
                            <Gift className="h-5 w-5" />
                          ) : transaction.amount > 0 ? (
                            <TrendingUp className="h-5 w-5" />
                          ) : (
                            <TrendingDown className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{transaction.description}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(transaction.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          transaction.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                        </p>
                        <p className="text-sm text-gray-500">
                          Bakiye: {transaction.balanceAfter}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <Card className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-6">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
              <Coins className="h-5 w-5" />
              Krediler Ne İşe Yarar?
            </h3>
            <ul className="text-sm text-amber-700 space-y-2">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Kulüp oluşturma (50 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Kulüp seviyesi yükseltme
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Özel kulüp logosu yükleme (100 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Kulüp ismi değişikliği (30 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Saha rezervasyonu (20 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Maça katılma (15 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Kafa Topu mini oyunu (2 kredi)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600" />
                Bireysel ünvanlar satın alma
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreditsPage;
